import React, { memo, useCallback, useState, useEffect } from 'react';
import { SideFilter } from 'components/Filter';

import {
  OverflowMenu,
  OverflowMenuItem,
  Row,
  Column,
  Button,
  Loading,
  ComboBox,
} from '@carbon/react';

import {
  InsertPage,
  Select_01,
  SettingsAdjust,
  TrashCan,
  Categories,
  CameraAction,
} from '@carbon/react/icons';
import { CheckedItem, SearchBar } from 'components/SearchBar';
import ListItem from 'components/List/ListItem';

import { useInView } from 'react-cool-inview';
import { useLazyGetTagsWithFilterQuery } from 'services/tags';
import { useGetElementsQuery } from 'services/elements';
import { openElementForm, openVariantGroupForm } from 'services/forms';
import { useGetCountsQuery } from 'services/tags';

import { useDispatch, useSelector } from 'react-redux';

import './_overrides.scss';
import './_elements-page.scss';
import useModal from '@/lib/hooks/useModal';
import { useSelectStream } from '@/lib/modal';
import { usePasteElementsMutation } from '@/redux/new_services/stream';
import { showToast } from '@/redux/new_services/toasts';
import useUser from '@/lib/hooks/useUser';
import { useGetGroupsQuery, useLazyGetGroupsQuery } from '@/redux/new_services/groups';
import { useGetOrganisationsQuery } from '@/redux/new_services/organisations';
import { useLinkPairMutation } from '@/redux/new_services/pair';
import { variantGroupTile } from './aggregateByPair';
import CombineSelectionModal from './CombineSelectionModal';

// Convert one server-side AggregatedTile into a Media-shaped object that
// the existing ListItem renderer can consume. Media tiles pass through
// unchanged except for normalizing the server's `pair_partner` field
// into the underscore-prefixed `_pair_partner` flag ListItem already
// knows. Variant-group tiles go through the existing variantGroupTile
// builder so the gallery sees identical objects whether they came from
// the legacy client-side aggregation or the new server-side one.
// `preferOrientation` keeps the visible primary half of a Hoch+Quer pair
// aligned with the operator's orientation filter (0 = Querformat/landscape,
// 1 = Hochkant/portrait). The server always emits the landscape half as the
// pair primary, so without this swap a "Hochkant"-filtered gallery would
// still show the landscape thumbnails of every pair.
const tileToRenderable = (tile, preferOrientation = 0) => {
  if (!tile) return null;
  if (tile.kind === 'media' && tile.media) {
    let primary = tile.media;
    let partner = primary.pair_partner || null;
    if (
      partner &&
      partner.orientation === preferOrientation &&
      primary.orientation !== preferOrientation
    ) {
      const newPrimary = { ...partner };
      const newPartner = { ...primary };
      delete newPrimary.pair_partner;
      delete newPartner.pair_partner;
      newPrimary.pair_partner = newPartner;
      primary = newPrimary;
      partner = newPartner;
    }
    return {
      ...primary,
      _pair_partner: partner,
      _is_pair: Boolean(partner),
      _is_variant_group: false,
    };
  }
  if (tile.kind === 'variant_group' && tile.variant_group) {
    return variantGroupTile(tile.variant_group, preferOrientation);
  }
  return null;
};

const Filter = ({
  groups = [],
  tags = [],
  counts = {},
  filters = [
    {
      title: 'Zeige ...',
      displayAs: 'radiobox',
      type: 'show',
      items: [
        { label: 'Alle', value: 'show_all', default: true},
        { label: 'Nur Neue', value: 'show_new'},
        { label: 'Nur noch nie Verwendete', value: 'show_unused'},
      ]
    },
    {
      title: 'Typen',
      type: 'options',
      displayAs: 'radiobox',
      items: [
        { label: 'Alle', value: 'type_all', default: true},
        { label: 'Bilder', value: 'type_images', count: counts.images || 0 },
        { label: 'Videos', value: 'type_videos', count: counts.videos || 0 },
        { label: 'Varianten-Gruppen', value: 'type_variant_groups' },
      ],
    },
    {
      title: 'Orientierung',
      displayAs: 'radiobox',
      type: 'orientation',
      items: [
        {
          label: 'Alle',
          value: 'orientation_all',
          default: true,
        },
        {
          label: 'Querformat',
          value: 'orientation_landscape',
        },
        {
          label: 'Hochkant',
          value: 'orientation_portrait',
        },
        {
          label: 'Multiformat',
          value: 'orientation_multi',
        },
      ],
    },
    {
      title: 'Sichtbarkeit',
      displayAs: 'radiobox',
      type: 'visibility',
      items: [
        { label: 'Alle', value: 'all', default: true},
        { label: 'Nur Private', value: 'private', count: counts.privat || 0 },
        {
          label: 'Nur Öffentliche',
          value: 'public',
          count: counts.public || 0,
        },
      ],
    },
    { title: 'Gruppen', type: 'groups', items: [...groups, {value: -1, label: "keine Zuordnung"}], onlyAdmin: true, disableCount: true},
    { title: 'Kategorien', type: 'categories', items: tags, },
  ],
  filterChanged = (_f) => {},
  isAdmin = false,
  organisations = [],
  organisationId = 0,
  onOrganisationChange = (_id) => {},
}) => {
  // Admin-only organisation switch. The default 0 = "öffentlich" mirrors the
  // backend's default admin scope (organisation_id = 0). Picking a customer
  // switches the gallery into "view as private content of org X", letting
  // admins curate or review per-customer media without logging in as them.
  // The `/users/organisations` endpoint returns a flat shape
  // (OrganisationsForFilter: { id, name, count_users, count_locations }) —
  // not the usual { model: { ID }, name } gorm wrapping.
  const organisationItems = [
    { id: 0, name: 'Öffentlich (Standard)' },
    ...((organisations || []).map(o => ({ id: o.id, name: o.name }))),
  ];
  const selectedOrganisation =
    organisationItems.find(o => o.id === organisationId) || organisationItems[0];

  // Render the org switcher inside SideFilter's prepend slot so it sits in
  // the same white container as the rest of the filter sections. ComboBox
  // (not Dropdown) gives admins a typeahead for long customer lists.
  const organisationPrepend =
    isAdmin && organisations && organisations.length > 0 ? (
      <>
        <legend className="cds--label">Kunde / Organisation</legend>
        <ComboBox
          id="filter_organisation"
          titleText=""
          placeholder="Organisation suchen…"
          items={organisationItems}
          selectedItem={selectedOrganisation}
          itemToString={(o) => (o ? o.name : '')}
          onChange={(e) => onOrganisationChange(e.selectedItem ? e.selectedItem.id : 0)}
        />
      </>
    ) : null;

  return (
    <Column lg={4} md={8} className="elements-page__sidefilter">
      <SideFilter
        filters={filters}
        onChange={(f) => filterChanged(f)}
        prependSlot={organisationPrepend}
      />
    </Column>
  );
};

const Searchable = memo(
  ({
    searchChanged = (_text) => {},
    onSelectionMode = () => {},
    selectionModeActive = false,
    onDeleteSelection = () => {},
    onAppendSelection = () => {},
    onSizeChanged = () => {},
    onChangeGroups = () => {},
    onCombineSelection = () => {},
    canCombine = false,
    currentSize = 'normal',
    hideSelectionFeatures = false,
    isAdmin = false,
  }) => {
    return (
      <SearchBar labelText="Elemente durchsuchen" searchChanged={searchChanged}>
        <OverflowMenu
          renderIcon={(props) => <SettingsAdjust size={32} {...props} />}
          flipped
          size="lg"
          aria-label="Größe der Listen Elemente anpassen">
          <OverflowMenuItem
            itemText={
              <CheckedItem checked={currentSize === 'small'} text="klein" />
            }
            onClick={() => onSizeChanged('small')}
          />
          <OverflowMenuItem
            itemText={
              <CheckedItem checked={currentSize === 'normal'} text="normal" />
            }
            onClick={() => onSizeChanged('normal')}
          />
          <OverflowMenuItem
            itemText={
              <CheckedItem checked={currentSize === 'large'} text="groß" />
            }
            onClick={() => onSizeChanged('large')}
          />
        </OverflowMenu>

        {!hideSelectionFeatures && (
          <>
            {selectionModeActive && (
              <>
                <Button
                  hasIconOnly
                  renderIcon={TrashCan}
                  kind="danger"
                  onClick={onDeleteSelection}
                  tooltipPosition="bottom"
                  iconDescription="Elemente löschen"></Button>
                {isAdmin && <Button
                  hasIconOnly
                  renderIcon={Categories}
                  kind="secondary"
                  onClick={onChangeGroups}
                  tooltipPosition="bottom"
                  iconDescription="Element einer Gruppezuordnen"></Button>
                }
                {isAdmin && <Button
                  hasIconOnly
                  renderIcon={CameraAction}
                  kind="secondary"
                  onClick={onCombineSelection}
                  disabled={!canCombine}
                  tooltipPosition="bottom"
                  iconDescription="Auswahl als Hoch+Quer-Paar verknüpfen oder zur Varianten-Gruppe zusammenführen"></Button>
                }
                <Button
                  hasIconOnly
                  renderIcon={InsertPage}
                  kind="secondary"
                  onClick={onAppendSelection}
                  tooltipPosition="bottom"
                  iconDescription="Elemente zu Stream hinzufügen"></Button>
              </>
            )}

            {selectionModeActive === false ? (
              <Button
                renderIcon={(props) => <Select_01 size={32} {...props}/>}
                onClick={onSelectionMode}>
                Elemente auswählen
              </Button>
            ) : (
              <Button
                renderIcon={(props) => <Select_01 size={32} {...props}/>}
                onClick={onSelectionMode}>
                Auswahl abbrechen
              </Button>
            )}
          </>
        )}
      </SearchBar>
    );
  }
);

const Gallery = ({
  isModal,
  isSearchable = true,
  showFilter = true,
  onSelectItem,
  size = 'normal',
}) => {
  const {isAdmin} = useUser();
  const [sizedValue, setSize] = useState(size);
  const [items, setItems] = useState([]);
  const [lastArgs, setLastArgs] = useState("");
  const [lastRequestID, setLastRequestID] = useState("");
  const [query, setQuery] = useState({
    skip: 0,
    // aggregated=true tells the server to collapse pairs and inject one
    // tile per variant group; the response carries `tiles` instead of
    // `data` and `skip`/`limit` now apply to tile count rather than raw
    // media rows.
    filter: { text: '', categories: [], aggregated: true },
    limit: 10,
  });
  const [totalSize, setTotalSize] = useState(0);

  const { added: elementAddedStatus, removed: elementRemovedStatus, changed: elementChangedStatus } = useSelector((state) => state.element.status);

  const [pasteElements, { isLoading: isPastingElements }] = usePasteElementsMutation();
  const [confirmRemoveElements] = useModal('DELETE_ELEMENTS');
  const [changeGroupsModal] = useModal('CHANGE_GROUPS_FOR_ELEMENTS');
  const selectStream = useSelectStream();

  // Pair- and variant-group-aggregation are now done server-side
  // (filter.aggregated=true), so the gallery no longer needs to fetch
  // the full variant-groups list separately and merge it client-side.
  // The previous double-query / aggregateByPair pipeline is gone; one
  // response carries everything pre-collapsed and pre-sorted.
  const [linkPair] = useLinkPairMutation();

  // Admin-only "view as customer" switcher in the filter sidebar. The list
  // is needed even when the dropdown is closed so the labels render on
  // initial mount.
  const { data: organisations = [] } = useGetOrganisationsQuery(undefined, { skip: !isAdmin });

  useEffect(() => {
    if (elementRemovedStatus || elementChangedStatus || elementAddedStatus) {
      setQuery(f => { return {...f, skip: 0}});
    }
  }, [elementAddedStatus, elementRemovedStatus, elementChangedStatus]);

  const {
    data: counts,
  } = useGetCountsQuery({refetchOnMountOrArgChange: true});

  const [loadGroups, {
    data: groups, //{data: groups},
  }] = useLazyGetGroupsQuery({refetchOnMountOrArgChange: true});

  const [
    triggerFilteredTags,
    {
      data: filteredTags,
    }
  ] = useLazyGetTagsWithFilterQuery();

  const {
    data = { tiles: [], count: 0 },
    originalArgs,
    requestId,
    isFetching,
  } = useGetElementsQuery(query, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isAdmin) {
      loadGroups();
    }
  }, [isAdmin, loadGroups]);

  useEffect(() => {
    // wir wissen das das ein neuer request ist....
    // somit müssten eigentlich auch die daten neu sein...
    // wenn nicht, dann ist es ein fehler
    if (lastRequestID && lastRequestID === requestId && isFetching) {
      return;
    }

    setLastRequestID(requestId);
    const pageTiles = data.tiles || [];
    if (originalArgs && originalArgs.skip === 0) {
      setItems([{
        items: pageTiles,
        args: originalArgs,
      }]);
    } else {
      if (originalArgs !== lastArgs && pageTiles.length > 0) {
        setItems((i) => [...i, {
          items: pageTiles,
          args: originalArgs,
        }]);
      }
    }
    if (originalArgs !== lastArgs) {
      setLastArgs(originalArgs);
    }
  }, [data]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    //console.info("new totalSize:", items.reduce((sum, inner) => sum + inner.items.length, 0));
    setTotalSize(items.reduce((sum, inner) => sum + inner.items.length, 0));
  }, [items]);

  const { observe } = useInView({
    threshold: 0.5, // default is 0
    // When the last item comes into the viewport, fetch the next page.
    // `count` is the total tile count (server applies skip/limit on
    // tiles, not on raw media rows, since we run with aggregated=true).
    onEnter: () => {
      const count = data?.count || 0;
      const theSize = items.reduce((sum, inner) => sum + inner.items.length, 0);
      if (count >= 0 && theSize < count && !isFetching) {
        setQuery(f => ({ ...f, skip: f.skip + 10 }));
      }
    },
  }); // https://www.npmjs.com/package/react-cool-inview#infinite-scroll

  let fixedHeight = {};
  let overflowScroll = {};

  if (isModal) {
    fixedHeight = {
      height: '100%',
    };
    overflowScroll = {
      overflow: 'auto',
    };
  }


  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionMode, setSelectionMode] = useState(isModal ? true: false);
  const [combineModalOpen, setCombineModalOpen] = useState(false);

  const changeSelectionMode = useCallback(() => {
    setSelectionMode((s) => !s);
  }, [setSelectionMode]);

  // Filter-Keys, fuer die SideFilter zustaendig ist. Wenn der Operator
  // einen Filter komplett abwaehlt, schickt SideFilter den Key nicht
  // mehr im selection-Objekt mit — ein naives Spread liesse den alten
  // Wert im query-State stehen ("kein Filter aktiv im UI, aber die
  // Galerie zeigt weiter gefiltert"). Wir setzen daher jeden bekannten
  // Key explizit aus der aktuellen selection: ist er drin und nicht
  // leer, uebernehmen wir; sonst loeschen wir ihn.
  const SIDEFILTER_KEYS = ['groups', 'categories', 'options', 'orientation', 'visibility', 'show', 'pairing'];

  const onFilterChanged = (selection = {}) => {
    // Tag-Vorschau-Query: bekommt die rohe Selection rein, damit die
    // Tag-Counts die uebrigen Filter mitbeachten.
    triggerFilteredTags({ ...selection });

    setQuery((f) => {
      const nextFilter = { ...f.filter };
      for (const k of SIDEFILTER_KEYS) {
        const v = selection[k];
        if (Array.isArray(v) && v.length > 0) {
          nextFilter[k] = v;
        } else {
          delete nextFilter[k];
        }
      }
      return { ...f, skip: 0, filter: nextFilter };
    });
  };

  // Admin-only filter: switch the gallery scope to a specific customer's
  // private pool. 0 (or unset) keeps the public pool. The backend honours
  // this only for admins, so it is safe to send unconditionally.
  const onOrganisationChange = (organisationId) => {
    setQuery(f => ({
      ...f,
      skip: 0,
      filter: { ...f.filter, organisation_id: organisationId || 0 },
    }));
  };

  const onSizeChanged = (newSize) => {
    setSize(newSize);
  }

  useEffect(() => {
    // initial query tags with filter (without filter)
    triggerFilteredTags({options:[]});
  }, [triggerFilteredTags]);


  useEffect(() => {
    if (selectionMode === false) {
      setSelectedItems([]);
    }
  }, [selectionMode]);

  const clickListItem = useCallback(
    (item) => {
      if (selectionMode === true) {
        selectItem(item);
        return;
      }
      // Variant-group tile: route to the dedicated group editor; the model
      // ID on a synthetic group tile points at the preview media, so use
      // the attached _variant_group object's real id.
      if (item._is_variant_group && item._variant_group?.model?.ID) {
        dispatch(openVariantGroupForm(item._variant_group.model.ID));
        return;
      }
      dispatch(openElementForm(item))
    },
    [selectionMode, selectItem, dispatch]
  );

  const clickDeleteSelection = useCallback(() => {
    //TODO: after successfull, disable selection!
    confirmRemoveElements({elements: selectedItems})
  }, [selectedItems, confirmRemoveElements]);


  const dispatch = useDispatch();

  const searchChanged = (searchtext) => {
    setQuery(f => { return {...f, skip: 0, filter: {...f.filter, text: searchtext}} });
  };

  const clickChangeGroups = useCallback(() => {
    changeGroupsModal({elements: selectedItems});
  }, [changeGroupsModal, selectedItems]);

  const clickCombineSelection = useCallback(async () => {
    if (selectedItems.length < 2) return;
    // Detect a perfect Hoch+Quer pair candidate — exactly two elements,
    // opposite orientations, neither already paired, neither multi-format.
    // In that case link them directly: no group, no name, just a pair.
    if (selectedItems.length === 2) {
      const [a, b] = selectedItems;
      const isPerfectPair =
        !a.pair_id && !b.pair_id &&
        a.orientation !== 2 && b.orientation !== 2 &&
        a.orientation !== b.orientation;
      if (isPerfectPair) {
        try {
          const result = await linkPair({ id: a.model.ID, partnerId: b.model.ID }).unwrap();
          dispatch(showToast({
            kind: 'success',
            title: 'Paar verknüpft',
            subtitle: `${a.name} und ${b.name} sind jetzt ein Hoch+Quer-Paar.`,
          }));
          // Surgical local merge: replace the two member tiles with a
          // single collapsed pair-tile in our `items` state, in place.
          // Keeps scroll position and selection context for an admin
          // who's burning through many pairs in a row — way nicer than
          // dropping back to page 1 after every successful link.
          //
          // The local update mirrors what the server would have
          // returned on a refetch: landscape half wins as primary, the
          // portrait half becomes pair_partner on the media object.
          // RTK Query's Elements/LIST invalidation still fires from the
          // mutation, but we suppress the reset here so the visible
          // list doesn't scroll.
          const newPairId = result?.pair_id;
          if (newPairId) {
            const idA = a.model.ID;
            const idB = b.model.ID;
            setItems((batches) => {
              // Locate the two halves in the loaded batches.
              let halfA = null;
              let halfB = null;
              for (const batch of batches) {
                for (const tile of (batch.items || [])) {
                  if (tile?.kind !== 'media') continue;
                  const mid = tile.media?.model?.ID;
                  if (mid === idA && !halfA) halfA = tile;
                  if (mid === idB && !halfB) halfB = tile;
                }
              }
              // If for some reason one half isn't in the locally loaded
              // pages (different filter applied since selection, etc.)
              // we silently bail — the RTK invalidation already fired,
              // a normal refetch will eventually catch up.
              if (!halfA || !halfB) return batches;

              const aIsLandscape = halfA.media?.orientation === 0;
              const bIsLandscape = halfB.media?.orientation === 0;
              let primaryTile = halfA;
              let partnerTile = halfB;
              if (bIsLandscape && !aIsLandscape) {
                primaryTile = halfB;
                partnerTile = halfA;
              }
              const primaryMedia = {
                ...primaryTile.media,
                pair_id: newPairId,
                pair_partner: {
                  ...partnerTile.media,
                  pair_id: newPairId,
                },
              };
              const mergedTile = {
                kind: 'media',
                media: primaryMedia,
                highlighted: Boolean(primaryTile.highlighted || partnerTile.highlighted),
                created_at: primaryTile.created_at,
              };

              // Walk batches: drop in mergedTile at the position of the
              // first half encountered, remove the other half.
              let inserted = false;
              return batches.map((batch) => {
                const next = [];
                for (const tile of (batch.items || [])) {
                  if (tile?.kind === 'media') {
                    const mid = tile.media?.model?.ID;
                    if (mid === idA || mid === idB) {
                      if (!inserted) {
                        next.push(mergedTile);
                        inserted = true;
                      }
                      continue;
                    }
                  }
                  next.push(tile);
                }
                return { ...batch, items: next };
              });
            });
          }
        } catch {
          dispatch(showToast({
            kind: 'error',
            title: 'Fehler',
            subtitle: 'Paar-Verknüpfung fehlgeschlagen.',
          }));
        }
        setSelectionMode(false);
        setSelectedItems([]);
        return;
      }
    }
    // Anything else is a variant-group decision — open the named-form modal.
    setCombineModalOpen(true);
  }, [selectedItems, linkPair, dispatch]);

  const closeCombineModal = useCallback(() => {
    setCombineModalOpen(false);
    setSelectionMode(false);
    setSelectedItems([]);
  }, []);

  const clickAppendSelectionToStream = useCallback(() => {
    selectStream((stream) => {
      pasteElements({
        streams: [stream.model.ID],
        elements: selectedItems.map((i) => i.model.ID)
      }).unwrap().then((res) => {
        dispatch(showToast({kind: 'success', title: 'Kopiert', caption: 'Element(e) wurde(n) kopiert.'}));
        setSelectionMode(false);
      }).catch((rejectError) => {
        dispatch(showToast({kind: 'error', title: 'Fehler', caption: `Element(e) konnte(n) nicht kopiert werden! (${rejectError.payload.data})`}))
      });
    })
  }, [selectedItems, dispatch, pasteElements, selectStream, setSelectionMode]);

  const selectItem = useCallback((item) => {
    setSelectedItems((s) => {
      if (s.find((i) => i.model.ID === item.model.ID)) {
        return s.filter((i) => i.model.ID !== item.model.ID);
      }
      let new_selections =  [...s, item];

      if (onSelectItem) {
        onSelectItem(new_selections);
      }

      return new_selections;
    });
  }, [onSelectItem]);

  return (
    <div className={isModal ? 'elements-page__modal' : ''}>
      {isSearchable && (
        <div className={isModal ? '' : 'page-sticky-toolbar'}>
          <Searchable
            searchChanged={searchChanged}
            currentSize={sizedValue}
            hideSelectionFeatures={isModal}
            selectionModeActive={selectionMode}
            onSelectionMode={changeSelectionMode}
            onSizeChanged={onSizeChanged}
            onDeleteSelection={clickDeleteSelection}
            onAppendSelection={clickAppendSelectionToStream}
            onChangeGroups={clickChangeGroups}
            onCombineSelection={clickCombineSelection}
            canCombine={selectedItems.length >= 2}
            isAdmin={isAdmin}
          />
        </div>
      )}
      <CombineSelectionModal
        open={combineModalOpen}
        elements={selectedItems}
        onClose={closeCombineModal}
      />
      <Row className={`elements-page__r2 `} >
        {showFilter && (
          <Filter
            filterChanged={onFilterChanged}
            tags={(filteredTags || []).filter(t => t.count > 0).map(t => { return {value: t.details.ID, label: t.label, count: t.count}})}
            counts={counts}
            isModal={isModal}
            groups={(groups ? groups.data : []).map(t => ({value: t.model.ID, label: t.name}))}
            isAdmin={isAdmin}
            organisations={organisations}
            organisationId={query.filter?.organisation_id || 0}
            onOrganisationChange={onOrganisationChange}
          />
        )}
        <Column style={{ ...fixedHeight, ...overflowScroll }}>
          <Row className="list_container">
            {(() => {
              // Server already returned each page sorted, filtered, pair-
              // collapsed and with one tile per variant group. We just
              // walk the loaded pages in order and turn each AggregatedTile
              // into the Media-shaped object ListItem renders.
              const flatTiles = items.flatMap((inner) => inner.items);
              // Radiobox filter → single-item array. Portrait filter flips the
              // preferred pair primary to the Hochkant half; everything else
              // keeps the server default (landscape primary).
              const orientationFilter = query.filter?.orientation || [];
              const preferOrientation = orientationFilter.includes('orientation_portrait') ? 1 : 0;
              let renderables = flatTiles
                .map((t) => tileToRenderable(t, preferOrientation))
                .filter(Boolean);
              // "Nur Varianten-Gruppen" is the only remaining client-side
              // filter — it's a UI affordance for the operator to see only
              // the synthetic group tiles, and the SQL `type_*` options
              // already passed through to the server.
              const opts = query.filter?.options || [];
              if (opts.includes('type_variant_groups')) {
                renderables = renderables.filter((t) => t._is_variant_group);
              }
              return renderables.map((item, idx) => (
                <ListItem
                  item={item}
                  key={`tile-${idx}-${item.model.ID}`}
                  name={item.name}
                  onClick={clickListItem}
                  hooverFooter
                  isSelected={selectedItems.find((i) => i.model.ID === item.model.ID) !== undefined}
                  size={sizedValue}
                  groups={groups ? groups.data : []}
                />
              ));
            })()}
            { (isFetching || isPastingElements)
              ? <Loading active={true} withOverlay={false} />
              : <div ref={observe}></div>
            }
          </Row>
        </Column>
      </Row>
    </div>
  );
};

export default Gallery;
